export function performanceDriversTitle(): string {
  return "Performance Drivers";
}

export function performanceDriversStraightLineExplanation(pointOfSail: string): string {
  const rankMetric = pointOfSail === "REACHING" ? "BSP polar%" : "VMG%";
  return (
    `Best-modes channel values for the selected configuration and point of sail, by TWS band. ` +
    `Outliers are removed using IQR (25th–75th percentiles). From that cleaned set we take the upper 10% by ${rankMetric} ` +
    `and show the weighted mean across matching CONFIG tags. TWS bands with fewer than 10 qualifying segments are excluded. ` +
    `Colors compare values down each column across TWS rows.`
  );
}

export function performanceDriversExplanation(_schemaVersion: number): string {
  return "Select a configuration and point of sail to compare channel averages and SHAP importance across TWS bands. Upwind and downwind models predict VMG (kph); reaching models predict BSP (kph). Training uses BIN 10 AVG segments with grade ≥ 2 (upwind/downwind) or grade ≥ 1 (reaching), grouped by config and TWS bin. Cells need at least 100 samples (50 in light air ≤ 20 kph) to train a model.";
}

export function performanceDriversModelFitExplanation(): string {
  return "";
}

export function performanceDriversModelValidationExplanation(): string {
  return (
    "Each column checks the model behind the beeswarm above for the selected TWS band. " +
    "RMSE is holdout prediction error (lower is better). Segments and Boats count training data; Test is the 20% holdout set. " +
    "The parity plot compares actual vs predicted on that holdout—points close to the diagonal mean the model generalizes well. " +
    "Trust badges reflect RMSE and whether holdout error is much worse than training error."
  );
}

export type PerformanceDriversBeeswarmExplanationKind = "static" | "dynamics" | "maneuver";

export function performanceDriversBeeswarmExplanation(params: {
  kind: PerformanceDriversBeeswarmExplanationKind;
  targetUnit: string;
  maneuverLabel?: string;
}): string {
  const target = performanceDriversTargetShortLabel(params.targetUnit);
  const performanceLabel = target === "VMG" ? "VMG performance" : `${target} performance`;

  if (params.kind === "static") {
    return (
      `How static boat settings on straight-line sailing relate to ${performanceLabel} in this TWS band. ` +
      `Each column is a setup channel; dot color is that setting’s value on a segment and horizontal position is its SHAP contribution to predicted ${target}. ` +
      `Use this to see which static settings the model ties most strongly to ${performanceLabel}.`
    );
  }

  if (params.kind === "dynamics") {
    return (
      `How changes in setup—channel variability (σ) across straight-line segments—relate to ${performanceLabel}. ` +
      `This is not the mean setting level but how much a channel moves segment to segment; higher or lower variability can push predicted ${target} up or down. ` +
      `Use this to see which settings matter when they are not held constant.`
    );
  }

  const maneuverName =
    params.maneuverLabel === "TACK"
      ? "tacks"
      : params.maneuverLabel === "GYBE"
        ? "gybes"
        : "maneuvers";

  return (
    `How maneuver statistics and phase metrics (drop, build, turn, accelerations, and related phases) relate to VMG% on fleet ${maneuverName} in this wind band. ` +
    "Each column is one maneuver metric; dot color is that statistic on a maneuver and horizontal position is its SHAP contribution to predicted VMG%. " +
    "Use this to see which phases and statistics the model ties most strongly to maneuver VMG performance."
  );
}

export function performanceDriversBeeswarmImportanceRankingExplanation(): string {
  return (
    "Drivers are numbered #1, #2, … by mean |SHAP| in this cell (#1 = strongest average impact on the model prediction). " +
    "Read rank order along the driver axis—most important first (left on wide layouts, top on narrow). " +
    "Dot position shows whether higher or lower values push prediction up or down; dot color shows the driver value on that segment or maneuver."
  );
}

export function performanceDriversModelFitTitle(): string {
  return "Model fit";
}

export function performanceDriversParityTitle(): string {
  return "Actual vs predicted (holdout)";
}

export function performanceDriversDateRangeLabel(
  dateRange?: { start: string; end: string }
): string | null {
  if (!dateRange?.start || !dateRange?.end) return null;
  return `${dateRange.start} – ${dateRange.end}`;
}

export function performanceDriversEmptyCellMessage(skipReason?: string): string {
  return skipReason ?? "No trained model for this configuration and point of sail. Try another selection or rebuild with more data.";
}

export function performanceDriversNoSnapshotMessage(): string {
  return "No performance drivers snapshot exists for this project yet. An administrator can run condition_model.py from Admin Script Execution to build one.";
}

export function performanceDriversHighlightSettingsLabel(): string {
  return "Highlight settings";
}

export function performanceDriversStaleSnapshotMessage(): string {
  return "This snapshot uses an older model format (before schema 6). An administrator can re-run condition_model.py from Admin Script Execution to rebuild with embedded straight-line best modes and the current table layout.";
}

export function performanceDriversHighlightSettingsHelp(): string {
  return "";
}

export function performanceDriversColorLegendLow(): string {
  return "Lower";
}

export function performanceDriversColorLegendHigh(): string {
  return "Higher";
}

/** Short target name for tooltips (e.g. VMG [KPH] → VMG). */
export function performanceDriversTargetShortLabel(targetUnit: string): string {
  const match = /^([A-Za-z]+)/.exec(targetUnit.trim());
  return match?.[1]?.toUpperCase() ?? targetUnit;
}

export function performanceDriversImportanceExplanation(targetUnit: string): string {
  const target = performanceDriversTargetShortLabel(targetUnit);
  return (
    `Shows which setup channels matter most for predicted ${target} in each TWS band. ` +
    `The number is that channel’s share of model driver impact in the row (0–100%, setup columns only). ` +
    `This is model sensitivity from SHAP, not measured speed on the water.`
  );
}

export function performanceDriversImportanceLegendLower(targetUnit: string): string {
  return `Lower setting → faster ${performanceDriversTargetShortLabel(targetUnit)}`;
}

export function performanceDriversImportanceLegendHigher(targetUnit: string): string {
  return `Higher setting → faster ${performanceDriversTargetShortLabel(targetUnit)}`;
}

export function performanceDriversImportanceCellTooltip(
  valueKph: number,
  targetUnit: string,
  sharePercent: number | null,
  directionCorrelation: number | null
): string {
  const target = performanceDriversTargetShortLabel(targetUnit);
  let text = `Avg |SHAP| ±${valueKph.toFixed(2)} kph on predicted ${target}.`;
  if (sharePercent != null && Number.isFinite(sharePercent)) {
    text += ` ${sharePercent.toFixed(0)}% of setup-driver impact in this TWS band.`;
  }
  const kind = directionCorrelation != null && Number.isFinite(directionCorrelation)
    ? directionCorrelation >= 0.2
      ? `Higher values tend to increase ${target} (r=${directionCorrelation.toFixed(2)}).`
      : directionCorrelation <= -0.2
        ? `Lower values tend to increase ${target} (r=${directionCorrelation.toFixed(2)}).`
        : `Direction unclear (r=${directionCorrelation.toFixed(2)}).`
    : "";
  if (kind) text += ` ${kind}`;
  return text;
}

export function performanceDriversTableTitle(
  configuration: string | undefined,
  pointOfSail: string,
  schemaVersion: number
): string {
  const parts: string[] = [];
  if (schemaVersion >= 2 && configuration) {
    parts.push(configuration);
  }
  parts.push(pointOfSail);
  return parts.join(" · ");
}

export function performanceDriversTargetUnit(pointOfSail: string): string {
  return pointOfSail === "REACHING" ? "BSP [KPH]" : "VMG [KPH]";
}

/** @deprecated Use performanceDriversTableTitle */
export function performanceDriversChartTitle(
  configuration: string | undefined,
  pointOfSail: string,
  _twsBin: number,
  schemaVersion: number
): string {
  return performanceDriversTableTitle(configuration, pointOfSail, schemaVersion);
}
